/**
 * projectContainerSectionsFolderEventToState
 *
 * Applies folder-level realtime events (create/delete/rename) to the
 * ProjectContainerSectionModel[] home-feed state for project containers.
 *
 * This keeps the section folder structure in sync with ProjectFolderEvent
 * updates from SignalR without refetching the entire page.
 */
import type {
  ProjectContainerSectionModel,
  ProjectContainerOverviewFolder
} from 'src/features/companyProjects/projectContainers/dashboard/sections/home/types/ProjectContainerHomeTypes';
import ProjectFolderType from 'src/enums/companyProjects/ProjectFolderType';
import type { ProjectFolderEvent } from './projectContainerEventTypes';
import { ProjectFolderEventType } from './projectContainerEventTypes';

// ----- Sections/home-feed helpers -----

const cloneSections = (
  state: ProjectContainerSectionModel[] | undefined
): ProjectContainerSectionModel[] | undefined => {
  if (!state) return state;
  return state.map((section) => ({
    ...section,
    Folders: section.Folders
      ? section.Folders.map<ProjectContainerOverviewFolder>((folder) => ({
          ...folder,
          Intakes: folder.Intakes ? [...folder.Intakes] : undefined
        }))
      : section.Folders
  }));
};

// ----- Sections/home-feed reducer (folder events) -----

export const applyFolderEventToSectionsState = (
  state: ProjectContainerSectionModel[] | undefined,
  event: ProjectFolderEvent
): ProjectContainerSectionModel[] | undefined => {
  const current = state;
  if (!current || current.length === 0) {
    return state;
  }

  const { folderId, type, name } = event;

  switch (type) {
    case ProjectFolderEventType.FolderCreated: {
      if (!name?.trim()) return state;

      // Prevent duplicates across all sections
      const exists = current.some((section) => section.Folders?.some((folder) => folder.Id === folderId));
      if (exists) {
        return state;
      }

      const next = cloneSections(current);
      if (!next || next.length === 0) return state;

      const targetSection = next.find((section) => section.Folders != null);
      if (!targetSection) {
        return state;
      }

      const folders = targetSection.Folders ?? [];
      const folderType = folders[0]?.FolderType ?? ProjectFolderType.Custom;

      const newFolder: ProjectContainerOverviewFolder = {
        Id: folderId,
        Name: name,
        FolderType: folderType,
        Intakes: []
      };

      const updatedSection: ProjectContainerSectionModel = {
        ...targetSection,
        Folders: [...folders, newFolder]
      };

      return next.map((section) => (section === targetSection ? updatedSection : section));
    }

    case ProjectFolderEventType.FolderDeleted: {
      let changed = false;
      const next = cloneSections(current);
      if (!next) return state;

      const sections = next.map((section) => {
        if (!section.Folders || section.Folders.length === 0) return section;

        const folders = section.Folders.filter((folder) => folder.Id !== folderId);
        if (folders.length === section.Folders.length) {
          return section;
        }

        changed = true;
        return {
          ...section,
          Folders: folders
        };
      });

      return changed ? sections : state;
    }

    case ProjectFolderEventType.FolderRenamed: {
      if (!name?.trim()) return state;

      let changed = false;
      const next = cloneSections(current);
      if (!next) return state;

      const sections = next.map((section) => {
        if (!section.Folders || section.Folders.length === 0) return section;

        let sectionChanged = false;
        const folders = section.Folders.map((folder) => {
          if (folder.Id !== folderId) return folder;
          sectionChanged = true;
          return {
            ...folder,
            Name: name
          };
        });

        if (sectionChanged) {
          changed = true;
          return {
            ...section,
            Folders: folders
          };
        }

        return section;
      });

      return changed ? sections : state;
    }

    default:
      return state;
  }
};
